import { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { authApi, setTokens, clearTokens, AUTH_EXPIRED_EVENT } from '../api/client';

const AuthContext = createContext(null);

// ─── Lecture sécurisée du user en localStorage ────────────────────────────────
const readStoredUser = () => {
  try {
    const raw = localStorage.getItem('lavex_user');
    if (!raw) return null;
    const u = JSON.parse(raw);
    // Vérification minimale : rôle présent et valide
    return u?.role === 'admin' ? u : null;
  } catch {
    return null;
  }
};

export const AuthProvider = ({ children }) => {
  const [user,    setUser]    = useState(readStoredUser);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');

  // ── Écouter l'expiration de session émise par le client API ──────────────
  // On utilise useRef pour avoir accès à la version à jour de setUser sans
  // re-enregistrer le listener à chaque render.
  const setUserRef = useRef(setUser);
  useEffect(() => { setUserRef.current = setUser; }, []);

  useEffect(() => {
    const onExpired = () => {
      // clearTokens() est déjà appelé dans handleAuthExpired() du client API
      setUserRef.current(null);
    };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []); // une seule fois au montage

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = useCallback(async (identifier, password) => {
    setLoading(true);
    setError('');
    try {
      const data = await authApi.login({ identifier, password });
      const u = data.data;

      // Seul le rôle "admin" est autorisé sur cette plateforme.
      if (u?.role !== 'admin') {
        setError('Accès refusé. Cette plateforme est réservée aux administrateurs LAVEX.');
        return { success: false };
      }

      setTokens(u.accessToken, u.refreshToken);
      localStorage.setItem('lavex_user', JSON.stringify(u));
      setUser(u);

      if (u.password_reset_required) {
        return { success: true, passwordResetRequired: true };
      }
      return { success: true };
    } catch (err) {
      setError(err.message || 'Identifiants invalides ou serveur inaccessible.');
      return { success: false };
    } finally {
      setLoading(false);
    }
  }, []);

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = useCallback(async () => {
    try {
      const rt = localStorage.getItem('lavex_refresh_token');
      if (rt) await authApi.logout({ refreshToken: rt });
    } catch {
      // Erreur réseau tolérée : on déconnecte quand même
    } finally {
      clearTokens();
      setUser(null);
    }
  }, []);

  // ── Changement de mot de passe obligatoire (v2.2) ─────────────────────────
  const updatePassword = useCallback(async (currentPassword, newPassword) => {
    await authApi.updatePassword({ currentPassword, newPassword });
    // Le backend invalide les tokens → on déconnecte proprement
    clearTokens();
    setUser(null);
  }, []);

  // ── Mise à jour locale des infos utilisateur ──────────────────────────────
  const refreshUser = useCallback((newData) => {
    setUser(prev => {
      const updated = { ...prev, ...newData };
      localStorage.setItem('lavex_user', JSON.stringify(updated));
      return updated;
    });
  }, []);

  return (
    <AuthContext.Provider value={{
      user,
      loading,
      error,
      setError,
      login,
      logout,
      updatePassword,
      refreshUser,
      passwordResetRequired: user?.password_reset_required === true,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth doit être utilisé dans un AuthProvider');
  return ctx;
};
